import { HttpClient, HttpResponse, HttpHeaders, HttpParams, HttpBackend } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { environment } from 'environments/environment';
import { map, Observable, retry } from 'rxjs';
import {
  Auth0Config,
  ConfigData,
  CountryConfig,
  COVEBO_MINIMAL_VERSION_HEADER,
  CultureInfo,
  GoogleApiKeys,
  LanguageInfo,
  MaintenanceConfig,
  PayslipTutorialUris,
} from '../types/config/config.types';
import { FeatureFlag } from '@app/core/feature-flags/feature-flag.types';
import { BRAND_ID_HEADER, OCP_APIM_SUBSCRIPTION_PARAM, X_HOHR_CLIENT_PLATFORM, X_HOHR_CLIENT_VERSION } from '../core.constants';
import { BRAND_ID } from '../config/brand.constants';
import { CountryISO } from 'ngx-intl-tel-input-gg';
import { FeatureFlagStore } from '../feature-flags/feature-flag.store';
import { GoogleMapsService } from './google-maps.service';
import { DeviceType } from '../types/common/device.types';
import { AuthClientConfig } from '@auth0/auth0-angular';
import { getClientInfo } from '@app/shared/utils/client-info.util';

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly http = inject(HttpClient);
  private readonly googleMapsService = inject(GoogleMapsService);
  private readonly injector = inject(Injector);
  private readonly handler = inject(HttpBackend);

  private readonly url = `${environment.url.api}/config`;

  public config: ConfigData;
  public languages: LanguageInfo[];
  public callbackUri: string;
  public playStoreLink: string;
  public appStoreLink: string;
  public payslipTutorialUris: PayslipTutorialUris;
  public minVersion: string;
  public cultureInfos: CultureInfo[] = [];
  public defaultCultureCode: string;
  public defaultAlpha3Code: string;
  public preferredCountries: CountryISO[];
  public defaultCountry: CountryConfig;
  public spotcheckDomain: string;
  public spotcheckTargetToken: string;

  async loadConfigClient() {
    return new HttpClient(this.handler)
      .get(`${this.url}/client`, {
        observe: 'response',
        headers: new HttpHeaders({
          [BRAND_ID_HEADER]: BRAND_ID,
          [X_HOHR_CLIENT_VERSION]: (await getClientInfo()).version,
          [X_HOHR_CLIENT_PLATFORM]: (await getClientInfo()).platform,
        }),
        params: new HttpParams().set(OCP_APIM_SUBSCRIPTION_PARAM, environment.ocpApimSubscriptionKey),
      })
      .pipe(
        map((config: HttpResponse<ConfigData>) => {
          this.minVersion = config.headers.get(COVEBO_MINIMAL_VERSION_HEADER);
          this.googleMapsService.load(
            this.getGoogleApiKey(config.body.keys.google)
          );
          this.playStoreLink = config.body.links.playStore;
          this.appStoreLink = config.body.links.appStore;
          this.cultureInfos = this.mapAlpha2Codes(config.body);
          this.defaultCultureCode = this.cultureInfos[0].cultureCode;
          this.defaultAlpha3Code = this.cultureInfos[0].languageAlpha3Code;
          this.featureFlagStore.updateFeatureFlags(config.body.featureFlags as FeatureFlag[]);
          this.config = config.body;
          // languages do need to be set here manually, otherwise observable languages$ will only emit an empty array
          this.languages = config.body.languages;
          this.payslipTutorialUris = config.body.paySlipTutorialUris;
          this.defaultCountry = config.body.defaultCountry;
          this.preferredCountries = config.body.preferredCountries?.map(
            (t) => t.alpha2Code.toLowerCase() as CountryISO
          );
          this.spotcheckDomain = config.body.spotCheck?.domain;
          this.spotcheckTargetToken = config.body.spotCheck?.targetToken;

          getClientInfo().then(({ id }) => {
            if (id) {
              this.callbackUri = `${id}://${config.body?.security?.auth0?.domain}/capacitor/${id}/callback`;
            } else {
              this.callbackUri = window.location.origin;
            }
          });

          return this.config;
        }),
        retry({ count: 3, delay: 10000 })
      );
  }

  auth0InitializerFn = (config: Auth0Config) => {
    const client = this.injector.get<AuthClientConfig>(AuthClientConfig);
    return client.set({
      domain: config?.domain || '',
      clientId: config?.clientId || '',
      authorizationParams: {
        audience: config?.audience || '',
        redirect_uri: config?.redirectUri || `${window.location.origin}`,
      },
      useRefreshTokens: config?.useRefreshTokens || true,
      cache: config?.cache || null,
      cacheLocation: config?.cacheLocation || 'localstorage',
      httpInterceptor: {
        allowedList: [
          ...(config?.securedRoutes ?? []).map((route) => `${this.url}${route}`),
          ...(config?.allowedList ?? []),
        ],
      },
    });
  };

  getMaintenanceConfig(): Observable<MaintenanceConfig> {
    return this.http.get<MaintenanceConfig>(`${this.url}/client/maintenance`);
  }

  private mapAlpha2Codes(bodyData: ConfigData): CultureInfo[] {
    return bodyData?.cultures.map((culture) => ({
      ...culture,
      languageId: bodyData.languages?.find(
        (language) => language.alpha2Code.toLowerCase() === culture.languageAlpha2Code.toLowerCase()
      )?.id,
      languageAlpha3Code: bodyData.languages?.find(
        (language) => language.alpha2Code.toLowerCase() === culture.languageAlpha2Code.toLowerCase()
      )?.alpha3Code,
    }));
  }

  private getGoogleApiKey(googleApiKeys: { ios: GoogleApiKeys; android: GoogleApiKeys; web: GoogleApiKeys }): string {
    switch (Capacitor.getPlatform()) {
      case DeviceType.IOS:
        return googleApiKeys.ios.mapsApiKey;
      case DeviceType.Android:
        return googleApiKeys.android.mapsApiKey;
      default:
        return googleApiKeys.web.mapsApiKey;
    }
  }
}
